// ~/api/appClient.ts
import AppConfig from '~/app.config.ts'; // Adjust the path accordingly
import md5 from 'md5';
import CookieManager from '~/utils/cookieManager.ts';
import { RegisterUserData, BuyNowProduct } from '~/types/index.ts'; // Adjust the import path accordingly
import { DiscountType } from '~/types/enums.ts'; // Adjust the import path accordingly

interface APIResponse {
  version: number;
  valid: boolean;
  code?: number;
  error?: string;
  result: any;
}

interface User {
  account_is_active: boolean;
  email_address: string;
  is_logged_in: boolean;
  name: string;
  discount?: {
    type: DiscountType;
    amount: number;
    ends: string | null; // If you have a specific date format or if you're planning to use a Date object, adjust this type accordingly.
  };
}

interface LoginResponse extends APIResponse {
  user?: User;
  login_error?: string;
}

interface UserCredentials {
  email_address: string;
  password: string;
}

interface ProductData {
  name: string;
  type: string;
}

interface CartData {
  product: string;
  quantity: number;
}

class Price {
  EUR: string;
  JPY: string;
  USD: string;
}

class UserData {
  editor_media_id: number;
  productbox_media_id: number;
}

class ProductDetails {
  download: {
      mac: string;
      win: string;
  };
  manual: string;
  name: string;
  price: Price;
  type: string;
  user_data: UserData;
  version: {
      mac: string;
      win: string;
  };
}

class ProductResponse {
  version: number;
  valid: boolean;
  code: number;
  error: string;
  result: ProductDetails;
}

interface IPResponse {
  ip_address: string;
  is_version_6: boolean;
}

export enum ActivityType {
  Download = "download",
  Registration = "registration",
  Review = "review",
  Shop = "shop"
}

export interface LiveStatsResponse {
  country: string;
  product?: Array<{
    name: string;
    thumbnail: string;
    website: string;
  }>;
  time: string;
  type: ActivityType;
}

export interface PromotionsResponse {
  products: Array<{
    description: string;
    discount: number;
    end_date: string;
    product_name: string;
    website: string;
  }>;
}


export interface RegisterUserData {
  accepted_tos: boolean;                  // User's acceptance of Terms of Service
  csrf_token: string;                     // A time-limited security token linking the form to the user session
  email_address: string;                 // User's email address
  first_name: string;                    // User's first name
  last_name: string;                     // User's last name
  password: string;                      // User's chosen password
  password_2: string;                    // User's chosen password, repeated
  subscribe_to_newsletter: boolean;      // User's subscription choice for newsletter
}

export enum CSRFType {
  USER_LOGIN = 'login',
  USER_REGISTER = 'register',
  UPDATE_USER_SETTINGS = 'updateProfile',
  UPDATE_NEWSLETTER_SETTINGS = 'updateNewsletterSettings',
  CHANGE_PASSWORD = 'changePassword',
  PASSWORD_FORGET = 'passwordForget',
  REDEEM_PRODUCT = 'redeemProduct',
  RATE_PRODUCT = 'rateProduct',
  DELETE_ACCOUNT = 'deleteAccount',
  CHANGE_MAIL = 'changeMail',
}

export default class APIClient {
  private static instance?: APIClient;  // this should be of type APIClient | undefined

  private ipInfo: IPResponse | undefined;
  private access_token: string;
  private cookieManager: CookieManager;
  private tokenExpireTimeHours: number = 2;

  private constructor() {
    this.cookieManager = CookieManager.getInstance();
    this.getIPAddress();
    // Initialize other properties if needed
  }

  public getTokenExpireTimeMs(): number {
    return this.tokenExpireTimeHours * 3600000; // 1 hour = 3600000 milliseconds
  }

  public static getInstance(): APIClient {
    if (!APIClient.instance) {
      APIClient.instance = new APIClient();
    }
    return APIClient.instance;
  }

  private getTokenFromCookie(): string {
    return this.cookieManager.getCookie('access_token') || '';
  }
  
  public setTokenToCookie(token: string): void {
    this.cookieManager.setNecessaryCookie('access_token', token, this.tokenExpireTimeHours / 24);
    this.access_token = token;
  }

  private createChecksum(globals: string, request: string): string {
    return md5(globals + request + import.meta.env.VITE_API_SECRET);
  }

  private async fetchAPI(endpoint: string, bodyData: any): Promise<APIResponse> {

    if (!process.client) {
      return;
    }

    if(this.ipInfo == null && !endpoint.includes(AppConfig.endpoints.user.ipAddress)) {
        try {
        //  console.log("No IP set, but endpoint ", endpoint, " is triggered. Fetching IP...");
          const IPResponse = await this.getIPAddress();
          this.ipInfo = IPResponse.result;
         // console.log("IPInfo: ", this.ipInfo?.ip_address);
      } catch (error) {
          console.error("Failed fetching IP:", error);
      }
    }
    const access_token = this.access_token;
    if ((access_token === "" || access_token === undefined) 
    && !endpoint.includes(AppConfig.endpoints.access.generateToken)
    && !endpoint.includes(AppConfig.endpoints.user.ipAddress)
    ) {
      //console.error("No Access Token but endpoint is not generateToken. Aborting. Endpoint is: ", endpoint);
      return;
    }
  
    const globalstring = JSON.stringify({ access_token: access_token, ip_address: this.ipInfo?.ip_address, url: window.location.pathname });
    const requeststring = JSON.stringify(bodyData);
    const checksumstring = this.createChecksum(globalstring, requeststring);

    //console.log("Sending request with: Globals: " + globalstring + " Request: " + requeststring + " Checksum: " + checksumstring);
    //console.log("Sending request to: " + endpoint);

    try {
        const response = await fetch(endpoint, { // using endpoint directly here
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ globals: globalstring, request: requeststring, checksum: checksumstring }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API request failed with status ${response.status}: ${errorText}`);
        }
        //console.log("Response: ", response);
        // Check if the response body is empty
        const responseBody = await response.text();
        if (!responseBody.trim()) {
            console.warn('Empty response body received.');
            return {};  // Or handle it however you like
        }
        
        // If it's not empty, try parsing it as JSON
        return JSON.parse(responseBody);
    } catch (error) {
        throw new Error(`API request failed: ${error.message}`);
    }
  }

  public async fetchCSRF(csrfType: CSRFType): Promise<APIResponse> {
    let endpoint;

    if (csrfType === CSRFType.REDEEM_PRODUCT) {
        endpoint = AppConfig.endpoints.product[csrfType];
    } else {
        endpoint = AppConfig.endpoints.user[csrfType];
    }

    if (endpoint) {
        console.log("Fetching CSRF for: ", endpoint);
        return this.fetchAPI(endpoint, { mode: 'prepare' });
    } else {
        throw new Error(`Endpoint ${csrfType} not found in appropriate endpoints.`);
    }
}
  
  public async generateToken(): Promise<APIResponse> {

    const access_token = this.getTokenFromCookie();
    if(access_token !== "" && access_token !== undefined) {
      console.log("generateToken triggered but old exists from cookie. Regenerating...", access_token);
      this.access_token = access_token;
      const updRsp = await this.updateSession("");
      console.log("Update Response: ", updRsp);
      if(updRsp.valid) {
        console.log("Update Response when is valid: ", updRsp);
        const regResponse = await this.regenerateToken();
        if(regResponse.valid) {
          console.log("Regenerate Response when is valid: ", regResponse);
          this.setTokenToCookie(regResponse.result.access_token);
          this.access_token = regResponse.result.access_token;
          return updRsp;
        }
      }
    }

    console.log("Generating Token without prior token set", AppConfig.endpoints.access.generateToken);
    const response = await this.fetchAPI(AppConfig.endpoints.access.generateToken, {});
    
    if (response && response.result && response.result.access_token) {
      console.log("Setting Token to Cookie"); 
      this.setTokenToCookie(response.result.access_token);
      this.access_token = response.result.access_token;
    }
    
    return response;
  }

  public async regenerateToken(): Promise<APIResponse> {
    console.log("Regenerating Token", AppConfig.endpoints.access.regenerateToken);
    const response = await this.fetchAPI(AppConfig.endpoints.access.regenerateToken, {});
    
    if (response && response.result && response.result.access_token) {
      console.log("Setting Token to Cookie after regeneration"); 
      this.setTokenToCookie(response.result.access_token);
    }
    
    return response;
  } 

  public async updateSession(emailAddress: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.access.update, { emailAddress });
  }

  public async getProductData(name?: string, type?: string): Promise<ProductResponse> {
    const response = await this.fetchAPI(AppConfig.endpoints.product.getData, { name, type });
    return response as ProductResponse;
  }

  public async getPluginDownloadURL(product: string, os: string, licenseId?: string): Promise<APIResponse> {
      return this.fetchAPI(AppConfig.endpoints.product.download, {
          type: 'plugin',
          product: product,
          os: os,
          license_id: licenseId
      });
  }

  public async getSamplePackDownloadURL(product: string, version: string, licenseId?: string): Promise<APIResponse> {
      return this.fetchAPI(AppConfig.endpoints.product.download, {
          type: 'samplepack',
          product: product,
          version: version
      });
  }

  public async getLicenseDownloadURL(product: string, licenseId: string): Promise<APIResponse> {
      return this.fetchAPI(AppConfig.endpoints.product.download, {
          type: 'license',
          product: product,
          license_id: licenseId
      });
  }

  public async verifyEmail(id: string, key: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.verifyMail, {
        id: id, key: key, email: 'fouad.santiago@meshfor.com'
    });
  }

  public async getAllProducts(): Promise<APIResponse> {
    const response = await this.fetchAPI(AppConfig.endpoints.product.getData, {});
    return response as APIResponse;
  }
  
  public async getAllProductsNew(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.product.getAllProducts, {});
  }
  
  public async getIPAddress(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.ipAddress, {});
  }

  public async passwordForget(email_address: string, csrf_token: string): Promise<APIResponse> {
    console.log("Password Forget: ", AppConfig.endpoints.user.passwordForget, "Mail", email_address, "CSRF", csrf_token);

    const response = await this.fetchAPI(AppConfig.endpoints.user.passwordForget, { email_address: email_address, csrf_token: csrf_token, mode: 'execute' });
    console.log("Password Forget Response: ", response);
    return response; 
  }

  public async isValidEmail(email: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.isValidEmail, { email });
  }

  public async resendActivationEmail(email: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.resendActivation, { email });
  }

  public async registerUser(userData: RegisterUserData): Promise<APIResponse> {
    const registerResponse = await this.fetchAPI(AppConfig.endpoints.user.register, { ...userData, mode: 'execute' });
    console.log("Register Response: ", registerResponse);
    if(registerResponse.valid) {
    
      const updResponse: APIResponse = await this.updateSession(userData.email_address);
      console.log("Update Response: ", updResponse);

    }
    return registerResponse;
  }

  public async fetchLoginCSRF(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.login, { mode: 'prepare'});
  }

  public async loginUser(credentials: UserCredentials, csrf_token: string): Promise<LoginResponse> {
    const response: APIResponse = await this.fetchAPI(AppConfig.endpoints.user.login, { ...credentials, mode: 'execute', csrf_token: csrf_token });
    const loginResponse: LoginResponse = {
      ...response,
      user: undefined,
      login_error: undefined
    };

    if(response.valid) {
      console.log("Login Response: ", response);
    
      const updResponse: APIResponse = await this.updateSession(credentials.email_address);
      console.log("Update Response: ", updResponse);

      if (updResponse.result && updResponse.result.user !== null) {
        const user = updResponse.result.user;

        if (user.email_address && !user.account_is_active) {
          loginResponse.login_error = "Looks like your account is not yet activated. Please ensure that you got our activation mail and clicked the link in it. If you didn't receive the mail, please check your spam folder. If you still can't find it, please get in contact via our contact form.";
        } else if (user.email_address && user.account_is_active) {
          loginResponse.user = user;
        } else {
          loginResponse.login_error = "Couldn't sign you in. Please check your mail and password.";
        }
      } else {
        // Absolute error
        loginResponse.login_error = "Fatal request error during login. Please get in contact via support@forward-audio.com.";
      }
    }

    return loginResponse;
  }

  public async logoutUser(): Promise<APIResponse> {
    const response = await this.fetchAPI(AppConfig.endpoints.user.logout, {});
    this.regenerateToken();
    return response;
  }

  public async getUserProducts(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.getProducts, {});
  }

  public async updateUserProfile(firstName: string, lastName: string, registrationName: string, csrf_token: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.updateProfile, {
      mode: 'execute',
      first_name: firstName,
      last_name: lastName,
      registration_name: registrationName,
      csrf_token: csrf_token,
    });
  }

  public async fetchUserProfile(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.updateProfile, {
      mode: 'prepare'
    });
  }

  public async updateNewsletterSettings(options: number, csrf_token: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.updateNewsletterSettings, {
      mode: 'execute',
      csrf_token: csrf_token,
      options: options
    });
  }

  public async fetchNewsletterSettings(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.updateNewsletterSettings, {
      mode: 'prepare'
    });
  }

  public async changePassword(passwordOld: string, passwordNew1: string, passwordNew2: string, csrf_token: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.changePassword, {
      password_old: passwordOld,
      password_new1: passwordNew1,
      password_new2: passwordNew2,
      csrf_token: csrf_token,
      mode: 'execute'
    });
  }

  public async rateProduct(product: string, rating: number, comment: string, csrf_token: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.rateProduct, {
      product: product,
      rating: rating,
      comment: comment,
      csrf_token: csrf_token,
      mode: 'execute'
    });
  }


  public async getLicenseTransfer(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.user.getLicenseTransfer, {});
  }

  public async getCart(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.cart.get, {});
  }

  public async clearCart(): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.cart.clear, {});
  }

  public async checkoutCart(backlink_url: string, landing_page_url: string): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.cart.checkout, {backlink_url, landing_page_url});
  }

  public async buyNow(products: BuyNowProduct[], backlink_url: string, landing_page_url: string): Promise<APIResponse> {
    console.log("BuyNowProducts are:", products);
    const payload = {
        products: products,
        backlink_url: backlink_url,
        landing_page_url: landing_page_url,
        stop_on_error: true
    };
    console.log("BuyNow Payload: ", payload);
    const response = await this.fetchAPI(AppConfig.endpoints.cart.buyNow, payload);
    console.log("BuyNow Response: ", response);
    return response;
}

  public async addProductToCart(product: string, quantity: number = 1): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.cart.addProduct, { product, quantity });
  }

  public async removeProductFromCart(product: string, quantity: number = 1): Promise<APIResponse> {
      return this.fetchAPI(AppConfig.endpoints.cart.removeProduct, { product, quantity });
  }

  public async addCouponToCart(couponData: any): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.cart.addCoupon, couponData);
  }

  public async removeCouponFromCart(couponData: any): Promise<APIResponse> {
    return this.fetchAPI(AppConfig.endpoints.cart.removeCoupon, couponData);
  }

    /**
   * Fetch live stats about sales, downloads, registrations, and reviews.
   * @param timespan - The desired timespan for statistics in hours.
   */
    public async getLiveStats(timespan: number): Promise<LiveStatsResponse> {
      const data = { integertimespan: timespan };
      return await this.fetchAPI(AppConfig.endpoints.product.getLiveStats, { timespan: timespan } );
    }
  
    /**
     * Fetch information about current product promotions.
     */
    public async getPromotions(): Promise<PromotionsResponse> {
      return await this.fetchAPI(AppConfig.endpoints.product.getPromotions, {});
    }
  
    /**
     * Redeem a product code.
     * @param mode - The mode of the API request (either "prepare" or "execute").
     * @param csrf_token - CSRF token for the request (required for 'execute' mode).
     * @param redeem_code - The redeem code for the product.
     */
    public async redeemProduct(csrf_token?: string, redeem_code?: string): Promise<string[]> {
      const data = { mode: 'execute', csrf_token, redeem_code };
      console.log("Redeem Product: ", data);
      return await this.fetchAPI(AppConfig.endpoints.product.redeemProduct, data);
    }
}
